import type { RoleplayMessageSave } from '@/api/contract'

export interface RoleplaySaveScope {
  ownerId: string
  conversationId: number
  expectedTailId: number | null
}

/** Only completed prose and its original write are retained; no model request. */
export interface PendingRoleplaySave {
  request: RoleplayMessageSave
  ownerId: string
  slotId: number | string
  variantId: number | string
  savedMessageId: number | null
  createdAt: string
  active: boolean
  state: 'saving' | 'pending'
  originalText?: string
}

export type RoleplaySaveRetryResult =
  | { status: 'saved' }
  | { status: 'deleted' }
  | { status: 'failed' }
  | { status: 'rejected'; submission: PendingRoleplaySave }

const memory = new Map<string, PendingRoleplaySave[]>()
const memoryOnly = new Set<string>()
const key = (scope: Pick<RoleplaySaveScope, 'ownerId' | 'conversationId'>) =>
  `storyteller:roleplay-saves:${scope.ownerId}:${scope.conversationId}`

export function pendingRoleplaySaves(scope: Pick<RoleplaySaveScope, 'ownerId' | 'conversationId'>): PendingRoleplaySave[] {
  const storageKey = key(scope)
  if (memoryOnly.has(storageKey)) return memory.get(storageKey) ?? []
  try {
    const value: unknown = JSON.parse(localStorage.getItem(storageKey) ?? '[]')
    if (!Array.isArray(value)) return memory.get(storageKey) ?? []
    return value.filter((item): item is PendingRoleplaySave => Boolean(item &&
      item.ownerId === scope.ownerId && item.request?.conversationId === scope.conversationId &&
      typeof item.request.requestId === 'string' && typeof item.request.content === 'string' &&
      (typeof item.slotId === 'string' || typeof item.slotId === 'number') &&
      (typeof item.variantId === 'string' || typeof item.variantId === 'number')))
  } catch { return memory.get(storageKey) ?? [] }
}

function write(scope: Pick<RoleplaySaveScope, 'ownerId' | 'conversationId'>, records: PendingRoleplaySave[]): boolean {
  const storageKey = key(scope)
  memory.set(storageKey, records)
  try {
    if (records.length) localStorage.setItem(storageKey, JSON.stringify(records))
    else localStorage.removeItem(storageKey)
    memoryOnly.delete(storageKey)
    return true
  } catch { memoryOnly.add(storageKey); return false }
}

export function retainRoleplaySave(record: PendingRoleplaySave): boolean {
  const scope = { ownerId: record.ownerId, conversationId: record.request.conversationId }
  const records = pendingRoleplaySaves(scope).filter(item => item.request.requestId !== record.request.requestId)
  return write(scope, [...records, record])
}

export function forgetRoleplaySaves(scope: Pick<RoleplaySaveScope, 'ownerId' | 'conversationId'>, requestIds: string[]) {
  return write(scope, pendingRoleplaySaves(scope).filter(item => !requestIds.includes(item.request.requestId)))
}

/** A database error acknowledges rejection. Transport/parse failures do not. */
export function isConfirmedSaveRejection(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) return false
  return typeof error.code === 'string' && /^[0-9A-Z]{5}$/.test(error.code) && !error.code.startsWith('08')
}

export class UserMessageSaveUncertain extends Error {
  constructor() { super('The message save could not be confirmed. Resolve its save before continuing.'); this.name = 'UserMessageSaveUncertain' }
}

export class UserMessageSaveRejected extends Error {
  readonly code = 'P0001'
  constructor() { super('The message was not saved.'); this.name = 'UserMessageSaveRejected' }
}
